import { defineNuxtPlugin } from '#app';
import { logger } from '~/utils/logger';

const WEBGL_CONTEXT_ERROR = 'Error creating WebGL context.';
const LOG_EVENT_NAME = 'webgl_context_creation_failed';
const PARENT_MESSAGE_TYPE = 'WebGLContextError';
const LOG_DEBOUNCE_MS = 1000;

export default defineNuxtPlugin((nuxtApp) => {
    if (process.server) return;

    const datadogRum = nuxtApp.$datadogRum;

    const lastLoggedAt = new Map<string, number>();
    let hasSentParentMessage = false;

    const supportsContext = (type: 'webgl' | 'webgl2') => {
        try {
            const canvas = document.createElement('canvas');
            return Boolean(canvas.getContext(type));
        } catch (_error) {
            return false;
        }
    };

    const createErrorId = () => {
        try {
            return crypto.randomUUID();
        } catch (_error) {
            return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        }
    };

    const collectRumContext = () => {
        try {
            const context = datadogRum?.getInternalContext?.();
            if (!context) return {};
            return {
                rumSessionId: context.session_id,
                rumApplicationId: context.application_id,
                rumViewId: context.view?.id ?? context.view_id,
            };
        } catch (_error) {
            return {};
        }
    };

    const collectRuntimeContext = (meta: Record<string, unknown>, errorId: string) => ({
        errorId,
        ...collectRumContext(),
        ...meta,
        url: window.location.href,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
        pixelRatio: window.devicePixelRatio,
        webgl1Available: supportsContext('webgl'),
        webgl2Available: supportsContext('webgl2'),
        visibilityState: document.visibilityState,
        timestamp: new Date().toISOString(),
    });

    const postRecoveryMessage = (payload: Record<string, unknown>) => {
        if (hasSentParentMessage) return;
        if (!window.parent || window.parent === window) return;

        try {
            window.parent.postMessage(
                {
                    type: PARENT_MESSAGE_TYPE,
                    data: {
                        action: 'request-remount',
                        ...payload,
                    },
                },
                '*'
            );
            hasSentParentMessage = true;
        } catch (error) {
            logger.warn('Failed to notify parent about WebGL context error', error);
        }
    };

    const logToDatadog = (message: string, attributes: Record<string, unknown>) => {
        logger.error(message, attributes);
    };

    const shouldLog = (signature: string) => {
        const now = Date.now();
        const last = lastLoggedAt.get(signature) ?? 0;
        if (now - last < LOG_DEBOUNCE_MS) {
            return false;
        }
        lastLoggedAt.set(signature, now);
        return true;
    };

    const getErrorSignature = (error: unknown) => {
        if (error instanceof Error) {
            return `${error.name}:${error.message}:${error.stack ?? ''}`;
        }
        if (typeof error === 'string') return `string:${error}`;
        return `unknown:${String(error)}`;
    };

    const handleWebGLError = (error: unknown, meta: Record<string, unknown> = {}) => {
        if (!error) return;

        const message = error instanceof Error ? error.message : String(error);
        if (message !== WEBGL_CONTEXT_ERROR) return;

        const signature = getErrorSignature(error);
        if (!shouldLog(signature)) return;

        const errorId = createErrorId();
        const payload = collectRuntimeContext(
            {
                ...meta,
                errorMessage: message,
                errorStack: error instanceof Error ? error.stack : undefined,
            },
            errorId
        );

        logToDatadog(LOG_EVENT_NAME, payload);
        postRecoveryMessage(payload);
    };

    const originalVueErrorHandler = nuxtApp.vueApp.config.errorHandler;
    nuxtApp.vueApp.config.errorHandler = (err, instance, info) => {
        handleWebGLError(err, {
            source: 'vue-error-handler',
            info,
            component: instance?.type?.name,
        });

        if (typeof originalVueErrorHandler === 'function') {
            originalVueErrorHandler(err, instance, info);
        }
    };

    window.addEventListener('error', (event) => {
        const error = event.error ?? event.message;
        handleWebGLError(error, { source: 'window-error' });
    });

    window.addEventListener('unhandledrejection', (event) => {
        handleWebGLError(event.reason, { source: 'unhandled-rejection' });
    });
});
